<template>
  <div class="text-center mt-[50px]">
    <div class="relative inline-block">
      <div class="relative inline-block -top-[40px] w-[427px] pt-[200%]">
        <Resource
          :src="character?.image_url"
          :transform-resource="transformImage"
          class="w-full absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2"
        />
        <Resource
          :src="getFeUrl('/youyan-platform/character-intro/character-detail/foot-shadow.png')"
          class="images-pc__shadow"
        />
        <InfoText
          v-if="!props.character?.config?.is_special_outfit"
          :text="$t('ren-wu')"
          class="absolute flex-row-reverse top-[16%] right-[10%] z-[10]"
          @click="
            edit({
              nav: CONFIG_NAV.ACTOR,
              tab: { [ACTOR_NAV.CHARACTER]: true },
            })
          "
        />
      </div>
      <div class="image-container">
        <SmallImage
          v-for="(img, index) in imgs"
          :key="img.url"
          :url="img.url"
          :hover-url="img.hoverUrl"
          :text="img.text"
          :position="index % 2 === 0 ? 'left' : 'right'"
          :class="index % 2 === 0 ? 'img-left' : 'img-right'"
          :disabled="props.character?.config?.is_special_outfit"
          @edit="edit(img.jumpInfo)"
        />
      </div>
    </div>
    <div class="flex justify-center items-center pc-only">
      <UpgradeButton
        v-if="props.character"
        @handle="emit('create-work')"
        :text="$t('mian-fei-shi-yong-ci-ren-wu')"
        :configType="props.character.config_type"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import UpgradeButton from '@/components/upgrade-button/index.vue';
import { capitalize } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import type { Character, CharacterCollection, CharacterXihe } from '~/api/interface/character';
import { ACTOR_NAV, CONFIG_NAV } from '~/constants';
import { createWorkByCharacter } from '~/features/createWork';
import { transformImage } from '~/utils/';
import type { EditorJumpInfo } from '~/utils/localStorage';
import InfoText from './info-text.vue';
import SmallImage from './small-image.vue';

interface ImageCompositionProps {
  character?: Character | null;
  characterXihe?: CharacterXihe;
}

const { t: $t } = useI18n();

const props = defineProps<ImageCompositionProps>();
const emit = defineEmits(['create-work']);

function getCollection(data: CharacterCollection[] | undefined, type: CharacterCollection['type']) {
  const res = data?.find(item => item.type === type);
  if (res && type === 'makeup_collection') {
    return res?.is_general?.TRUE ? res : undefined;
  }
  return res;
}

interface ImgType {
  url: string;
  text: string;
  hoverUrl?: string;
  jumpInfo: EditorJumpInfo<typeof CONFIG_NAV.ACTOR>;
}

const imgs = computed(() => {
  const _imgs: ImgType[] = [];
  const gender = props.character?.config?.gender;

  // 发型
  const hairCollection = getCollection(props.characterXihe?.collection_list, 'hair_collection');

  if (hairCollection?.render_image_oss_pro) {
    // 说是一定有
    _imgs.push({
      url: hairCollection.render_image_oss_pro,
      text: $t('fa-xing'),
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.HAIR]: true,
        },
      },
    });
  }

  const collection = getCollection(props.characterXihe?.collection_list, 'collection');

  // 套装
  if (collection?.render_image_oss_pro) {
    // 说是一定有
    _imgs.push({
      url: collection.render_image_oss_pro || '',
      text: $t('tao-zhuang'),
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.COLLECTION]: true,
        },
      },
    });
  }

  // 面容
  _imgs.push({
    url:
      props.characterXihe?.render_facial_oss_pro ||
      getFeUrl(isChinese() ? '/youyan-platform/png/face.png' : '/rendora/avatar/face.png'),
    text: $t('mian-rong'),
    jumpInfo: {
      nav: CONFIG_NAV.ACTOR,
      tab: {
        [ACTOR_NAV.ADJUST]: props.characterXihe?.can_pinch,
        [ACTOR_NAV.CHARACTER]: !props.characterXihe?.can_pinch,
      },
    },
  });

  // 耳饰(耳坠，耳钉，耳环三个中最多只有一个)
  const earringsData = props.characterXihe?.accessory_info?.find(item =>
    ['Earrings', 'Eardrop', 'Stud'].includes(item.spu__category__name),
  );

  if (earringsData) {
    _imgs.push({
      url: earringsData.render_image_oss_pro || '',
      text: isChinese()
        ? earringsData.spu__category__cn_name
        : capitalize(earringsData.spu__category__cn_name),
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.ACCESSORY]: {
            type: earringsData.spu__category__name,
            id: earringsData.id.toString(),
          },
        },
      },
    });
  } else {
    _imgs.push({
      url: getFeUrl(
        isChinese()
          ? `/youyan-platform/png/earrings-2025.png`
          : `/rendora/avatar/earrings-2025.png`,
      ),
      hoverUrl: getFeUrl(
        isChinese()
          ? `/youyan-platform/png/earrings-2025-hover.png`
          : `/rendora/avatar/earrings-2025-hover.png`,
      ),
      text: $t('er-shi'),
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.ACCESSORY]: true,
        },
      },
    });
  }

  // 眼镜
  const glassesData = props.characterXihe?.accessory_info?.find(
    item => item.spu__category__name === 'Glasses',
  );

  if (glassesData) {
    _imgs.push({
      url: glassesData.render_image_oss_pro || '',
      text: isChinese()
        ? glassesData.spu__category__cn_name
        : capitalize(glassesData.spu__category__cn_name),
      // navKey: `${ACTOR_NAV.ACCESSORY}-${glassesData.spu__category__name}-${glassesData.id}`,
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.ACCESSORY]: {
            type: glassesData.spu__category__name,
            id: glassesData.id.toString(),
          },
        },
      },
    });
  } else {
    _imgs.push({
      url: getFeUrl(
        isChinese() ? '/youyan-platform/png/glasses-2025.png' : '/rendora/avatar/glasses-2025.png',
      ),
      hoverUrl: getFeUrl(
        isChinese()
          ? '/youyan-platform/png/glasses-2025-hover.png'
          : '/rendora/avatar/glasses-2025-hover.png',
      ),
      text: $t('yan-jing'),
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.ACCESSORY]: true,
        },
      },
    });
  }

  const makeupCollection = getCollection(props.characterXihe?.collection_list, 'makeup_collection');

  // 妆容
  if (makeupCollection?.render_image_oss_pro) {
    // 说是一定有
    _imgs.push({
      url: makeupCollection.render_image_oss_pro,
      text: $t('zhuang-rong'),
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.MAKEUP]: true,
        },
      },
    });
  } else {
    _imgs.push({
      url: getFeUrl(
        isChinese()
          ? `/youyan-platform/png/makeup-${gender === 'Male' ? 'male' : 'female'}-2025.png`
          : `/rendora/avatar/makeup-${gender === 'Male' ? 'male' : 'female'}-2025.png`,
      ),
      hoverUrl: getFeUrl(
        isChinese()
          ? `/youyan-platform/png/makeup-${gender === 'Male' ? 'male' : 'female'}-2025-hover.png`
          : `/rendora/avatar/makeup-${gender === 'Male' ? 'male' : 'female'}-2025-hover.png`,
      ),
      text: $t('zhuang-rong'),
      jumpInfo: {
        nav: CONFIG_NAV.ACTOR,
        tab: {
          [ACTOR_NAV.MAKEUP]: true,
        },
      },
    });
  }

  return _imgs;
});

function edit(jumpInfo: EditorJumpInfo<typeof CONFIG_NAV.ACTOR>) {
  if (!jumpInfo) return;
  createWorkByCharacter(props.character!.id, jumpInfo);
}
</script>
<style lang="scss" scoped>
.image-container {
  @apply w-[180%] h-[60%] absolute left-1/2 -translate-x-1/2 -translate-y-1/2;
  top: 45%;
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* 两列 */
  grid-template-rows: repeat(3, 1fr); /* 三行 */
  gap: 10px; /* 控制间距 */
  justify-items: center; /* 水平居中对齐 */
  align-items: center; /* 垂直居中对齐 */
}
.images-pc__shadow {
  @apply absolute  left-1/2 -translate-x-1/2 -bottom-[40px];
  width: 400px;
  height: 286px;
}

.img-left {
  left: -50%;
  transform: translateX(50%);
}

.img-right {
  right: -50%;
  transform: translateX(-50%);
}

.img-pointer {
  position: absolute;
  width: calc(50% + 165px);
  top: 50%;
  right: 50%;
  transform: translate(0, -50%) rotate(var(--rotateDeg));
  transform-origin: right center;

  & > div {
    transform: rotate(var(--reverseDeg));
  }
}
</style>
